import React, { ReactNode } from "react";
import { Alert, Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { ArrowClockwise, Clipboard, ClipboardCheck } from "react-bootstrap-icons";
import Select, { SingleValue } from "react-select";
import { Navigate } from "react-router-dom";
import { BusinessRulesOptions, LinkFormProps, LinkFormState } from "../../types/appTypes";
import {
	BlockPoliciesEnum,
	KanbanizeBlockReason,
	KanbanizeBoard,
	KanbanizeCardType,
	KanbanizeColumn,
	KanbanizeCustomField,
	KanbanizeError,
	RepoLink,
} from "../../types/kanbanizeTypes";
import {
	boards2SelectFormat,
	doGet,
	doPost,
	doPut,
	isActualError,
	isKanbanizeError,
	populateRepos,
	repository2SelectFormat,
} from "../../utils/utils";

export default class LinkForm extends React.Component<LinkFormProps, LinkFormState> {
	constructor(props: LinkFormProps) {
		super(props);
		const originalValues = this.props.currentConfig;
		console.log(`repository: ${originalValues.repo_name}`);
		const formState: LinkFormState = {
			repoId: originalValues.repo_id ? originalValues.repo_id.toString() : "0",
			repoName: originalValues.repo_name || "",
			board: originalValues.board_id ? originalValues.board_id.toString() : "",
			baseBranch: originalValues.base_branch ? originalValues.base_branch.toString() : "",
			blockPolicy: originalValues.block_policy || "noBlock",
			blockReason: originalValues.custom_block_reason_id ? originalValues.custom_block_reason_id.toString() : "",
			targetMergedPrCol: originalValues.merged_pr_column_id ? originalValues.merged_pr_column_id.toString() : "",
			targetReworkPrCol: originalValues.rework_pr_column_id ? originalValues.rework_pr_column_id.toString() : "",
			targetReworkPrType: originalValues.rework_pr_type_id ? originalValues.rework_pr_type_id.toString() : "",
			targetReworkPrPrefix: originalValues.rework_prefix ? originalValues.rework_prefix : "",
			targetBranchCreateCardCol: originalValues.create_from_branch_column_id
				? originalValues.create_from_branch_column_id.toString()
				: "",
			branchLinkCF: originalValues.branch_link_cf_id ? originalValues.branch_link_cf_id.toString() : "",
			prLinkCF: originalValues.pr_link_cf_id ? originalValues.pr_link_cf_id.toString() : "",
			optionsBlocks: [],
			optionsBoards: boards2SelectFormat(this.props.boards),
			optionsBranch: [],
			optionsCardTypes: [],
			optionsCFs: [],
			optionsColumns: [],
			optionsRepo: repository2SelectFormat(this.props.repositories),
			validated: false,
			returnToTable: false,
			successMessage: false,
			showCreateBRModal: false,
			optionsBRColumns: [],
			columnForPRCreate: "",
			columnForBranchCreate: "",
			createTheBusinessRules: originalValues.repo_name ? false : true,
			repoNameCFForBRCreate: "",
			appInstallWarning: true,
			externalKey: "",
			showExternalKeyModal: false,
			externalKeyCopied: false,
			showExternalKeyConfirmModal: false,
			showNoBranchWarning: false,
			targetBranchCreateCardLane: originalValues.create_from_branch_lane_id
				? originalValues.create_from_branch_lane_id.toString()
				: "",
			targetReworkPrLane: originalValues.rework_pr_lane_id ? originalValues.rework_pr_lane_id.toString() : "",
			optionsLaneCreateCard: [],
			optionsLaneReworkPr: [],
			kanbanizeColsAndLanes: [],
		};
		this.state = formState;
		this.props.setCurrentComponent("LinkForm");
		//Not optimum but will get the job done for now.
		this.fillSelects(true);
	}

	fillSelects = async (constructor: boolean) => {
		try {
			if (!constructor) await clearCache(this.props.integrationUrl);
			await Promise.all([this.refreshRepos(this.state.repoName), this.refreshBoards(false)]);
		} catch (e: any) {
			this.handleError(e.message);
		}
	};

	fillBoardRelatedSelects = async (refresh: boolean) => {
		if (!this.state.board) {
			return;
		}
		let options: { value: string; label: string }[][] | null;
		const boardDataMap = this.props.boardDataMap[this.state.board];
		const repoDataMap = this.props.repoDataMap[this.state.repoName];
		if (!refresh && boardDataMap) {
			options = [boardDataMap.columns, boardDataMap.cardTypes, boardDataMap.blocks, boardDataMap.customFields];
			if (repoDataMap) {
				options.push(repoDataMap.branches);
			} else {
				const branches_options = await populateBranches(
					this.props.integrationUrl,
					this.props.userToken,
					this.state.repoId,
					this.state.repoName,
					this.handleError,
				);
				options.push(branches_options);
			}
			options.push(boardDataMap.brColumns);
		} else {
			options = await this.populateBoardRelatedCombos();
			if (!options) return;
			this.props.addBoardDataMap(this.state.board, {
				columns: options[0],
				cardTypes: options[1],
				blocks: options[2],
				customFields: options[3],
				brColumns: options[5],
			});
			this.props.addRepoDataMap(this.state.repoName, {
				branches: options[4],
			});
		}
		const optionsColumns = options[0];
		const optionsCardTypes = options[1];
		const optionsBlocks = options[2];
		const optionsCFs = options[3];
		const optionsBranch = options[4];
		const optionsBRColumns = options[5];
		let state = {
			optionsBlocks,
			optionsBranch,
			optionsCardTypes,
			optionsCFs,
			optionsColumns,
			optionsBRColumns,
			optionsLaneReworkPr: populateLanesByColumn(this.state.kanbanizeColsAndLanes, Number(this.state.targetReworkPrCol)),
			optionsLaneCreateCard: populateLanesByColumn(
				this.state.kanbanizeColsAndLanes,
				Number(this.state.targetBranchCreateCardCol),
			),
		} as LinkFormState;
		this.setState(state);
	};

	inputChange = (value: SingleValue<{ value: string }>, name: string) => {
		try {
			if (!value) {
				return;
			}
			const checkFields = ["createTheBusinessRules"];
			const stateValue: string | boolean = checkFields.includes(name) ? value.value === "yes" : value.value;

			this.setState({ [name]: stateValue } as unknown as Pick<LinkFormState, keyof LinkFormState>, async () => {
				if (name === "board") {
					//Update columns
					const cols_with_lanes: KanbanizeColumn[] = await getColumnsWithLanes(
						this.props.integrationUrl,
						Number(this.state.board),
					);

					this.setState({
						kanbanizeColsAndLanes: cols_with_lanes,
						blockReason: "",
						targetMergedPrCol: "",
						targetReworkPrCol: "",
						targetReworkPrType: "",
						columnForPRCreate: "",
						columnForBranchCreate: "",
						repoNameCFForBRCreate: "",
						branchLinkCF: "",
						prLinkCF: "",
					});
					const boardDataMap = this.props.boardDataMap[this.state.board];
					if (boardDataMap) {
						this.setState({
							optionsColumns: boardDataMap.columns,
							optionsCardTypes: boardDataMap.cardTypes,
							optionsBlocks: boardDataMap.blocks,
							optionsCFs: boardDataMap.customFields,
							optionsBRColumns: boardDataMap.brColumns,
						});
					} else {
						let options: { value: string; label: string }[][] | null;
						options = await this.populateBoardRelatedCombos();
						if (!options) return;
						const [optionsColumns, optionsCardTypes, optionsBlocks, optionsCFs, , optionsBRColumns] = options;
						this.props.addBoardDataMap(this.state.board, {
							columns: optionsColumns,
							cardTypes: optionsCardTypes,
							blocks: optionsBlocks,
							customFields: optionsCFs,
							brColumns: optionsBRColumns,
						});
						this.setState({
							optionsColumns,
							optionsCardTypes,
							optionsBlocks,
							optionsCFs,
							optionsBRColumns,
						});
					}
				}
				// Fill the lane selects.
				if (name === "targetReworkPrCol") {
					this.setState({
						targetReworkPrLane: "",
						optionsLaneReworkPr: populateLanesByColumn(this.state.kanbanizeColsAndLanes, Number(stateValue)),
					});
				}
				if (name === "targetBranchCreateCardCol") {
					this.setState({
						targetBranchCreateCardLane: "",
						optionsLaneCreateCard: populateLanesByColumn(this.state.kanbanizeColsAndLanes, Number(stateValue)),
					});
				}
			});
		} catch (e: any) {
			this.handleError(e.message);
		}
	};

	blockPolicyChange = (val: string) => {
		this.setState({ blockPolicy: val });
	};

	repoNameChange = async (value: SingleValue<{ value: string; label: string }>) => {
		try {
			if (!value) {
				return;
			}
			const repoName = value.label;
			const repoId = this.state.optionsRepo.filter(item => item.label === repoName)[0].value;
			const optionsBranch = await populateBranches(
				this.props.integrationUrl,
				this.props.userToken,
				repoId,
				repoName,
				this.handleError,
			);
			this.setState({ baseBranch: "" });
			this.setState({ repoName, repoId }, () => {
				if (optionsBranch)
					this.setState({ optionsBranch, showNoBranchWarning: !!this.state.repoName && optionsBranch.length === 0 });
			});
		} catch (e: any) {
			this.handleError(e.message);
		}
	};

	submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
		try {
			event.preventDefault();
			const form = event.currentTarget;

			this.props.setGeneralError(false);

			if (!this.state.repoName) throw new Error("Please select the target repository");
			if (!this.state.baseBranch) throw new Error("Please select the base branch");
			if (!this.state.board) throw new Error("Please select the board");

			if (this.state.createTheBusinessRules) {
				if (!this.state.columnForBranchCreate) {
					throw new Error("Please select branch business rule target column");
				}

				if (!this.state.columnForPRCreate) {
					throw new Error("Please select PR business rule target column");
				}

				if (!this.state.repoNameCFForBRCreate) {
					throw new Error("Please select custom field with the full repository name");
				}
			}

			this.setState({ validated: true });
			if (form.checkValidity() === false) {
				event.stopPropagation();
			} else {
				const newConfig: RepoLink = {
					id: this.props.currentConfig.id,
					repo_id: parseInt(this.state.repoId),
					repo_name: this.state.repoName,
					base_branch: this.state.baseBranch,
					block_policy: this.state.blockPolicy as BlockPoliciesEnum,
					board_id: parseInt(this.state.board),
					merged_pr_column_id: parseInt(this.state.targetMergedPrCol),
					rework_pr_column_id: parseInt(this.state.targetReworkPrCol),
					branch_link_cf_id: parseInt(this.state.branchLinkCF),
					custom_block_reason_id: parseInt(this.state.blockReason),
					pr_link_cf_id: parseInt(this.state.prLinkCF),
					rework_pr_type_id: parseInt(this.state.targetReworkPrType),
					rework_prefix: this.state.targetReworkPrPrefix,
					create_from_branch_column_id: parseInt(this.state.targetBranchCreateCardCol),
					rework_pr_lane_id: Number(this.state.targetReworkPrLane),
					create_from_branch_lane_id: Number(this.state.targetBranchCreateCardLane),
				} as RepoLink;

				let linkUpdateJson;
				const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
				if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
				if (integration !== "GitHub") {
					const updateUrl = `${this.props.integrationUrl}/api/db/repoLink`;
					if (newConfig.id) {
						linkUpdateJson = await doPut(updateUrl, newConfig);
					} else {
						linkUpdateJson = await doPost(updateUrl, newConfig);
					}
				} else {
					const updateUrl = `${this.props.integrationUrl}/api/addRepoLink`;
					linkUpdateJson = await doPost(updateUrl, newConfig, this.props.userToken);
				}

				const [kanbanError, normalError] = [isKanbanizeError(linkUpdateJson), isActualError(linkUpdateJson)];
				if (kanbanError || normalError) {
					let msg = "";
					if (kanbanError) msg = (linkUpdateJson as KanbanizeError).error.message;
					if (normalError) msg = (linkUpdateJson as Error).message;
					this.handleError(msg);
					return;
				}
				if (this.state.createTheBusinessRules) {
					const brPayload: BusinessRulesOptions = {
						columnForBranchCreate: this.state.columnForBranchCreate,
						columnForPRCreate: this.state.columnForPRCreate,
						boardId: parseInt(this.state.board),
						repoName: this.state.repoNameCFForBRCreate,
					};
					let addBusinessRulesUrl;
					const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
					if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
					if (integration !== "GitHub") {
						addBusinessRulesUrl = `${this.props.integrationUrl}/api/kanbanize/createBusinessRules`;
						await doPost(addBusinessRulesUrl, brPayload);
					} else {
						addBusinessRulesUrl = `${this.props.integrationUrl}/api/createBusinessRules`;
						await doPost(addBusinessRulesUrl, brPayload, this.props.userToken);
					}
				}
				this.handleSuccess("The configuration has been updated correctly");
			}
		} catch (e: any) {
			this.handleError(e.message);
		}
	};

	populateBoardRelatedCombos = async (): Promise<{ value: string; label: string }[][] | null> => {
		const boardId = parseInt(this.state.board);
		try {
			let options: ({ value: string; label: string }[] | KanbanizeColumn[])[];
			let timer = Date.now();
			options = await Promise.all([
				populateCardTypes(this.props.integrationUrl, this.props.userToken, boardId, this.handleError),
				populateBlockReasons(this.props.integrationUrl, this.props.userToken, boardId, this.handleError),
				populateCustomFields(this.props.integrationUrl, this.props.userToken, boardId, this.handleError),
				populateBranches(
					this.props.integrationUrl,
					this.props.userToken,
					this.state.repoId,
					this.state.repoName,
					this.handleError,
				),
				populateColumnsBR(this.props.integrationUrl, this.props.userToken, boardId, this.handleError),
			]);
			console.log(`Board combos loaded in ${Date.now() - timer}ms`);
			return [
				populateColumns(this.state.kanbanizeColsAndLanes),
				options[0] ? (options[0] as { value: string; label: string }[]) : [],
				options[1] ? (options[1] as { value: string; label: string }[]) : [],
				options[2] ? (options[2] as { value: string; label: string }[]) : [],
				options[3] ? (options[3] as { value: string; label: string }[]) : [],
				options[4] ? (options[4] as { value: string; label: string }[]) : [],
			];
		} catch (error: any) {
			this.handleError(error.message);
			return null;
		}
	};

	handleError = (message: string) => {
		this.props.setGeneralError(message);
		//Since the form is long we would not notice an error at the top.
		//So we scroll to the top on the event of an error.
		window.scrollTo(0, 0);
	};

	handleSuccess = (message: string) => {
		this.setState({ successMessage: message });
	};

	hideSuccessAlert = () => {
		this.setState({ successMessage: false });
	};

	setReturnToTable = () => {
		this.setState({ returnToTable: true });
	};

	regenerateExternalAuthKey = async () => {
		let extAuthRegenUrl = `${this.props.integrationUrl}/api/generateExternalKey`;
		if (process.env.REACT_APP_INTEGRATION_SERVICE === "GitLab") {
			extAuthRegenUrl = `${this.props.integrationUrl}/api/db/generateExternalAuthKey`;
		}
		const data = await doPost(extAuthRegenUrl, { domain: this.props.currentConfig.kanbanize_domain }, this.props.userToken);

		this.setState({ externalKey: data.newAuthKey, externalKeyCopied: false, showExternalKeyModal: true });
	};

	copyExternalAuthKey = async () => {
		await navigator.clipboard.writeText(this.state.externalKey);
		this.setState({ externalKeyCopied: true });
	};

	getSelectedOption = (value: string, listOptions: SingleValue<{ value: string }>[]) => {
		if (!listOptions) return null;
		if (listOptions.constructor !== Array) return null; // not array
		for (const nextOption of listOptions) {
			if (nextOption && nextOption.value === value) return nextOption;
		}
		return null;
	};

	refreshRepos = async (default_reponame: string = "") => {
		const optionsRepo = await populateRepos(this.props.integrationUrl, this.props.userToken);
		this.props.setRepositories(optionsRepo);
		this.setState({ optionsRepo: repository2SelectFormat(optionsRepo) }, () => {
			populateBranches(
				this.props.integrationUrl,
				this.props.userToken,
				this.state.repoId,
				this.state.repoName,
				this.handleError,
			).then(optionsBranch =>
				this.setState({
					optionsBranch,
					repoName: default_reponame,
					showNoBranchWarning: !!this.state.repoName && optionsBranch.length === 0,
				}),
			);
		});
	};
	refreshBoards = async (refresh: boolean) => {
		const optionsBoards = await populateBoardCombo(this.props.integrationUrl, this.props.userToken, this.handleError);
		this.props.setBoards(
			optionsBoards.map(item => {
				return {
					board_id: parseInt(item.value),
					name: item.label,
				};
			}),
		);
		if (this.state.board && !isNaN(Number(this.state.board))) {
			const cols_with_lanes: KanbanizeColumn[] = await getColumnsWithLanes(
				this.props.integrationUrl,
				Number(this.state.board),
			);
			this.setState({ kanbanizeColsAndLanes: cols_with_lanes });
		}
		this.setState({ optionsBoards }, () => {
			this.fillBoardRelatedSelects(refresh);
		});
	};

	render(): React.ReactNode {
		// const blockPolicy = [
		//     {name: 'No block policy', value:'noBlock'},
		//     {name: 'Custom block policy', value:'custom'}
		// ];

		const {
			optionsBRColumns,
			optionsBranch,
			optionsBlocks,
			optionsBoards,
			optionsCFs,
			optionsCardTypes,
			optionsColumns,
			optionsRepo,
			optionsLaneCreateCard,
			optionsLaneReworkPr,
		} = this.state;
		const { id } = this.props.currentConfig;

		return (
			<>
				{/* {!this.props.userToken && <Navigate to="/login" replace={true} />} */}
				{this.state.returnToTable && <Navigate to="/currentLinks" replace={true} />}
				<Form noValidate validated={this.state.validated} onSubmit={this.submitForm}>
					{!id && (
						<Alert
							show={this.state.appInstallWarning}
							onClose={() => this.setState({ appInstallWarning: false })}
							variant="warning"
							dismissible
						>
							If you can't see your repo, make sure that you have installed our App in your repository
						</Alert>
					)}
					<Alert
						show={this.state.showNoBranchWarning}
						onClose={() => this.setState({ showNoBranchWarning: false })}
						variant="warning"
						dismissible
					>
						Current repository has no branches. please, create at least one branch.
					</Alert>
					<fieldset className="mb-3">
						<legend>Main fields</legend>
						<div className="row">
							<Form.Group className="mb-3 col">
								<Form.Label>* Repository:</Form.Label>
								<Select
									required
									isDisabled={!!this.props.currentConfig.id}
									backspaceRemovesValue={true}
									options={optionsRepo}
									onChange={this.repoNameChange}
									value={optionsRepo.find(item => item.label === this.state.repoName)}
								/>
							</Form.Group>
							<Form.Group className="mb-3 col">
								<Form.Label>* Base branch:</Form.Label>
								<Row>
									<Col md={11}>
										<Select
											required
											options={optionsBranch}
											onChange={val => this.inputChange(val, "baseBranch")}
											value={optionsBranch.find(item => item.value === this.state.baseBranch)}
										/>
										<Form.Control.Feedback>Looks good!</Form.Control.Feedback>
										<Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
									</Col>
									<Col className="d-flex p-0" md={1}>
										<Button variant="outline-primary" id="button-addon1" onClick={() => this.refreshRepos()}>
											<ArrowClockwise />
										</Button>
									</Col>
								</Row>
							</Form.Group>
						</div>
						<Form.Group className="mb-3">
							<Form.Label>* Target board for the repository:</Form.Label>
							<Row>
								<div style={{ width: "96%" }}>
									<Select
										required
										options={optionsBoards}
										onChange={val => this.inputChange(val, "board")}
										value={optionsBoards.find(item => item.value === this.state.board)}
									/>
								</div>
								<div className="d-flex p-0" style={{ width: "4%" }}>
									<Button
										variant="outline-primary"
										id="button-addon1"
										onClick={() => {
											this.refreshBoards(true);
										}}
									>
										<ArrowClockwise />
									</Button>
								</div>
							</Row>
							<Form.Control.Feedback>Looks good!</Form.Control.Feedback>
							<Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
						</Form.Group>
					</fieldset>
					<fieldset className="mb-3">
						<legend>About branches</legend>
						<div className="row">
							<Form.Group className="mb-3 col-md-12">
								<Form.Label>Save branch link in custom field:</Form.Label>
								<Select
									options={optionsCFs}
									onChange={val => this.inputChange(val, "branchLinkCF")}
									value={this.getSelectedOption(this.state.branchLinkCF, optionsCFs)}
								/>
							</Form.Group>
						</div>
					</fieldset>
					<fieldset className="mb-3">
						<legend>
							About {process.env.REACT_APP_INTEGRATION_SERVICE === "GitLab" ? "merge requests" : "pull requests"}
						</legend>
						<div className="row">
							<Form.Group className="mb-3 col-6">
								<Form.Label>
									Save {process.env.REACT_APP_INTEGRATION_SERVICE === "GitLab" ? "MR" : "PR"} link in custom field:
								</Form.Label>
								<Select
									options={optionsCFs}
									onChange={val => this.inputChange(val, "prLinkCF")}
									value={this.getSelectedOption(this.state.prLinkCF, optionsCFs)}
								/>
							</Form.Group>
							<Form.Group className="mb-3 col-6">
								<Form.Label>
									Target column for cards of merged{" "}
									{process.env.REACT_APP_INTEGRATION_SERVICE === "GitLab" ? "MR" : "PR"}s
								</Form.Label>
								<Select
									required
									options={optionsColumns}
									onChange={val => this.inputChange(val, "targetMergedPrCol")}
									value={this.getSelectedOption(this.state.targetMergedPrCol, optionsColumns)}
								/>
								<Form.Control.Feedback>Looks good!</Form.Control.Feedback>
								<Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
							</Form.Group>
						</div>
					</fieldset>
					<fieldset className="mb-3">
						<legend>Rework configuration</legend>
						<div className="row">
							<Form.Group className="mb-3 col-6">
								<Form.Label>Target column for new reworks:</Form.Label>
								<Select
									required
									options={optionsColumns}
									onChange={val => this.inputChange(val, "targetReworkPrCol")}
									value={this.getSelectedOption(this.state.targetReworkPrCol, optionsColumns)}
								/>
								<Form.Control.Feedback>Looks good!</Form.Control.Feedback>
								<Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
							</Form.Group>
							<Form.Group className="mb-3 col-6">
								<Form.Label>Target lane for new reworks:</Form.Label>
								<Select
									required
									options={optionsLaneReworkPr}
									onChange={val => this.inputChange(val, "targetReworkPrLane")}
									value={this.getSelectedOption(this.state.targetReworkPrLane, optionsLaneReworkPr)}
								/>
								<Form.Control.Feedback>Looks good!</Form.Control.Feedback>
								<Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
							</Form.Group>
						</div>
						<div className="row">
							<Form.Group className="mb-3 col-6">
								<Form.Label>Card type for the rework:</Form.Label>
								<Select
									required
									options={optionsCardTypes}
									onChange={val => this.inputChange(val, "targetReworkPrType")}
									value={this.getSelectedOption(this.state.targetReworkPrType, optionsCardTypes)}
								/>
							</Form.Group>
							<Form.Group className="mb-3 col-6">
								<Form.Label>Rework prefix:</Form.Label>
								<Form.Control
									value={this.state.targetReworkPrPrefix}
									onChange={val => this.inputChange({ value: val.target.value }, "targetReworkPrPrefix")}
								></Form.Control>
							</Form.Group>
						</div>
					</fieldset>
					<fieldset className="mb-3">
						<legend>Manual branch tracking</legend>
						<div className="row">
							<Form.Group className="mb-3 col-6">
								<Form.Label>Target column for new cards:</Form.Label>
								<Select
									required
									options={optionsColumns}
									onChange={val => this.inputChange(val, "targetBranchCreateCardCol")}
									value={this.getSelectedOption(this.state.targetBranchCreateCardCol, optionsColumns)}
								/>
							</Form.Group>
							<Form.Group className="mb-3 col-6">
								<Form.Label>Target lane for new cards:</Form.Label>
								<Select
									required
									options={optionsLaneCreateCard}
									onChange={val => this.inputChange(val, "targetBranchCreateCardLane")}
									value={this.getSelectedOption(this.state.targetBranchCreateCardLane, optionsLaneCreateCard)}
								/>
							</Form.Group>
						</div>
					</fieldset>
					<fieldset className="mb-3">
						<legend>Block configuration</legend>
						<div className="row">
							<Form.Group className="mb-3 col-md-6">
								<Form.Check
									type="radio"
									label="No block policy"
									name="blockPolicy"
									checked={this.state.blockPolicy === "noBlock"}
									onChange={ev => {
										if (ev.target.checked) {
											this.blockPolicyChange("noBlock");
										}
									}}
								/>
								<Form.Check
									type="radio"
									label="Custom block policy"
									name="blockPolicy"
									checked={this.state.blockPolicy === "custom"}
									onChange={ev => {
										if (ev.target.checked) {
											this.blockPolicyChange("custom");
										}
									}}
								/>
							</Form.Group>
							<Form.Group className="mb-3 col-md-6">
								<Form.Label>Block reason:</Form.Label>
								<Select
									isDisabled={this.state.blockPolicy !== "custom"}
									options={optionsBlocks}
									onChange={val => this.inputChange(val, "blockReason")}
									value={this.getSelectedOption(this.state.blockReason, optionsBlocks)}
								/>
							</Form.Group>
						</div>
					</fieldset>

					{!id && (
						<fieldset className="mb-3">
							<legend>Business rules</legend>
							<div className="row">
								<Form.Group>
									<Form.Check
										checked={this.state.createTheBusinessRules}
										className="my-3"
										type="switch"
										onChange={ev => {
											this.inputChange({ value: ev.target.checked ? "yes" : "no" }, "createTheBusinessRules");
										}}
										label={((): ReactNode => {
											//NOTE: We use a self called function here to properly add the link,
											//else its complicated to add the link with the call to setState
											return (
												<p>
													Create business rules. To configure the business rule click{" "}
													<a
														href="https://businessmap.io"
														onClick={e => {
															e.preventDefault();
															this.setState({ showCreateBRModal: true });
														}}
													>
														here
													</a>
												</p>
											);
										})()}
										name="createTheBusinessRules"
									/>
								</Form.Group>
							</div>
						</fieldset>
					)}
					<p>
						Note: To authorize the comunication between Businessmap business rules and our integration we use an
						authorization token. To reset this token (Having to change it in Businessmap's business rules) click{" "}
						<a
							href="https://businessmap.io"
							onClick={e => {
								e.preventDefault();
								this.setState({ showExternalKeyConfirmModal: true });
							}}
						>
							here
						</a>
					</p>
					<div className="row justify-content-center">
						<Button className="m-1 m-3 col-3" variant="light" onClick={this.setReturnToTable}>
							Return
						</Button>
						<Button className="m-1 m-3 col-3" variant="primary" type="submit">
							Save
						</Button>
					</div>
				</Form>
				<Modal show={this.state.showCreateBRModal} onHide={() => this.setState({ showCreateBRModal: false })}>
					<Modal.Header closeButton> Select the target columns</Modal.Header>
					<Modal.Body>
						<strong>Please, select the columns to apply the business rules.</strong>
						<div className="row">
							<Form.Group className="mb-3">
								<Form.Label>* Create branch business rule target column:</Form.Label>
								<Select
									options={optionsBRColumns}
									onChange={val => this.inputChange(val, "columnForBranchCreate")}
									value={this.getSelectedOption(this.state.columnForBranchCreate, optionsBRColumns)}
								/>
							</Form.Group>
							<Form.Group className="mb-3">
								<Form.Label>
									* Create {process.env.REACT_APP_INTEGRATION_SERVICE === "GitLab" ? "MR" : "PR"} business rule target
									column:
								</Form.Label>
								<Select
									options={optionsBRColumns}
									onChange={val => this.inputChange(val, "columnForPRCreate")}
									value={this.getSelectedOption(this.state.columnForPRCreate, optionsBRColumns)}
								/>
							</Form.Group>
							<Form.Group className="mb-3">
								<Form.Label>* Custom field with the full repository name:</Form.Label>
								<Select
									options={optionsCFs}
									onChange={val => this.inputChange(val, "repoNameCFForBRCreate")}
									value={this.getSelectedOption(this.state.repoNameCFForBRCreate, optionsCFs)}
								/>
							</Form.Group>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={() => this.setState({ showCreateBRModal: false })}>
							Close
						</Button>
					</Modal.Footer>
				</Modal>
				<Modal
					show={this.state.showExternalKeyConfirmModal}
					onHide={() => this.setState({ showExternalKeyConfirmModal: false })}
				>
					<Modal.Header closeButton> Confirm </Modal.Header>
					<Modal.Body>
						<Alert variant="warning">
							<Alert.Heading>WARNING</Alert.Heading>
							<p>All existing integrations links of {this.props.currentConfig.kanbanize_domain} will be affected</p>
							<p>
								If you proceed, you will have to update the authentication key in the existing business rules in
								Businessmap. The new authentication key value will be updated if you click "Confirm"
							</p>
						</Alert>
					</Modal.Body>
					<Modal.Footer>
						<Button
							className="mb-3 m-3 col-4"
							variant="light"
							onClick={() => this.setState({ showExternalKeyConfirmModal: false })}
						>
							Dismiss
						</Button>
						<Button
							className="mb-3 m-3 col-4"
							variant="primary"
							onClick={() => this.setState({ showExternalKeyConfirmModal: false }, this.regenerateExternalAuthKey)}
						>
							Confirm
						</Button>
					</Modal.Footer>
				</Modal>
				<Modal
					show={this.state.showExternalKeyModal}
					onHide={() => this.setState({ showExternalKeyModal: false, externalKey: "" })}
				>
					<Modal.Header closeButton> Please copy the new token below</Modal.Header>
					<Modal.Body>
						<InputGroup className="mb-3">
							<Form.Control value={this.state.externalKey} disabled={true}></Form.Control>
							<Button variant="outline-primary" id="button-addon2" onClick={this.copyExternalAuthKey}>
								{(this.state.externalKeyCopied && <ClipboardCheck />) || <Clipboard />}
							</Button>
						</InputGroup>
					</Modal.Body>
				</Modal>
				<Modal
					show={!!this.state.successMessage}
					onHide={() => {
						this.setState({ successMessage: false });
					}}
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header>Success</Modal.Header>
					<Modal.Body>
						<Alert show={true} variant="success">
							<p>{this.state.successMessage}</p>
						</Alert>
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={() => this.setState({ successMessage: false })}>
							Close
						</Button>
					</Modal.Footer>
				</Modal>
			</>
		);
	}
}

async function populateBranches(
	integrationUrl: string,
	authToken: string,
	repoId: string,
	repoName: string,
	errorHandler: (msg: string) => void,
) {
	try {
		if (!repoId || !repoName) {
			return [];
		}
		let data: any;
		let branchNames: string[];
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		if (integration === "GitLab") {
			data = await doGet(`${integrationUrl}/api/${integration.toLowerCase()}/repo/${repoId}/branches`);
			branchNames = data.map((item: any) => item.name);
		} else if (integration === "BitBucket") {
			data = await doGet(`${integrationUrl}/api/${integration.toLowerCase()}/repo/${repoName}/branches`);
			branchNames = data.map((item: any) => item.name);
		} else {
			if (!repoName) return [];
			branchNames = await doGet(`${integrationUrl}/api/repo/${repoName}/branches`, authToken);
		}
		if (isKanbanizeError(branchNames) || isActualError(branchNames)) {
			let message = "";
			if (isKanbanizeError(branchNames)) message = (branchNames as KanbanizeError).error.message;
			if (isActualError(branchNames)) message = (branchNames as Error).message;
			throw new Error(message);
		}
		return branchNames.map(branch => {
			return { value: branch, label: branch };
		});
	} catch (e: any) {
		return Promise.reject(e);
		//throw new Error(e.message);
	}
}

async function populateBoardCombo(integrationUrl: string, authToken: string, errorHandler: (msg: string) => void) {
	console.log("populateBoardCombo: init");
	try {
		let boards: KanbanizeBoard[];
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		if (integration !== "GitHub") {
			boards = await doGet(`${integrationUrl}/api/kanbanize/boards`);
		} else {
			boards = await doGet(`${integrationUrl}/api/availableBoards`, authToken);
		}

		return boards.map(board => {
			return { value: board.board_id.toString(), label: board.name };
		});
	} catch (e: any) {
		throw new Error(e.message);
	}
}

async function getColumnsWithLanes(integration_url: string, board_id: number) {
	try {
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		let columns: KanbanizeColumn[] = [];
		if (integration !== "GitHub") {
			columns = await doGet(`${integration_url}/api/kanbanize/columns?board_id=${board_id}`);
		} else {
			const payload = {
				board_id: board_id,
			};
			columns = await doPost(`${integration_url}/api/columns`, payload);
		}
		return columns;
	} catch (e: any) {
		return Promise.reject(e);
	}
}

function populateLanesByColumn(columns_obj: KanbanizeColumn[], column_id: number): { value: string; label: string }[] {
	try {
		if (!column_id) return [];
		const lanes = columns_obj.filter(nxt_col => nxt_col.id === column_id)[0].lanes_in_workflow;
		return lanes.map(nxt_lane => ({ value: nxt_lane.id.toString(), label: nxt_lane.name }));
	} catch (e: any) {
		throw e;
	}
}

function populateColumns(columns_obj: KanbanizeColumn[]): { value: string; label: string }[] {
	try {
		const valueList = columns_obj.map(col => {
			return { value: col.id.toString(), label: col.columnPath };
		});
		valueList.unshift({
			value: "",
			label: "-",
		});
		return valueList;
	} catch (e: any) {
		throw e;
	}
}

async function populateColumnsBR(
	integrationUrl: string,
	authToken: string,
	boardId: number,
	errorHandler: (msg: string) => void,
): Promise<{ value: string; label: string }[]> {
	try {
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		if (integration !== "GitHub") {
			const columns: { value: string; label: string }[] = await doGet(
				`${integrationUrl}/api/kanbanize/columns/br?board_id=${boardId}`,
			);
			return columns.map(item => {
				return { value: item.value, label: item.label };
			});
		} else {
			const payload = {
				board_id: boardId,
			};
			const result = await doPost(`${integrationUrl}/api/columns/br`, payload, authToken);
			return result;
		}
	} catch (e: any) {
		return Promise.reject(e);
		//throw new Error(e.message);
	}
}

async function populateCardTypes(
	integrationUrl: string,
	authToken: string,
	boardId: number,
	errorHandler: (msg: string) => void,
) {
	let cardTypes: KanbanizeCardType[];
	try {
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		if (integration !== "GitHub") {
			cardTypes = await doGet(`${integrationUrl}/api/kanbanize/cardTypes?board_id=${boardId}`);
		} else {
			const payload = {
				board_id: boardId,
			};

			cardTypes = await doPost(`${integrationUrl}/api/cardTypes`, payload, authToken);
		}

		const nullType = {
			type_id: "",
			name: "-",
		};
		cardTypes.unshift(nullType as unknown as KanbanizeCardType); //We cast the nullType so it wont make a fuss about not having the iconId for example

		return cardTypes.map(ct => {
			return { value: ct.type_id.toString(), label: ct.name };
		});
	} catch (e: any) {
		return Promise.reject(e);
		//throw new Error(e.message);
	}
}

async function populateBlockReasons(
	integrationUrl: string,
	authToken: string,
	boardId: number,
	errorHandler: (msg: string) => void,
) {
	try {
		let blockReasons: KanbanizeBlockReason[];
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		if (integration !== "GitHub") {
			blockReasons = await doGet(`${integrationUrl}/api/kanbanize/blockReasons?board_id=${boardId}`);
		} else {
			const payload = {
				board_id: boardId,
			};
			blockReasons = await doPost(`${integrationUrl}/api/blockReasons`, payload, authToken);
		}

		return blockReasons.map(block => {
			return { value: block.reason_id.toString(), label: block.label };
		});
	} catch (e: any) {
		return Promise.reject(e);
		//throw new Error(e.message);
	}
}

async function populateCustomFields(
	integrationUrl: string,
	authToken: string,
	boardId: number,
	errorHandler: (msg: string) => void,
) {
	try {
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		let customFields: KanbanizeCustomField[];
		if (integration !== "GitHub") {
			customFields = await doGet(`${integrationUrl}/api/kanbanize/customFields?board_id=${boardId}`);
		} else {
			const payload = {
				board_id: boardId,
			};
			customFields = await doPost(`${integrationUrl}/api/customFields`, payload, authToken);
		}

		const nullCF = {
			field_id: "",
			name: "-",
		};
		customFields.unshift(nullCF as unknown as KanbanizeCustomField); //We cast the nullType so it wont make a fuss about not having the iconId for example

		return customFields.map(cf => {
			return { value: cf.field_id.toString(), label: cf.name };
		});
	} catch (e: any) {
		return Promise.reject(e);
		//throw new Error(e.message);
	}
}

async function clearCache(integrationUrl: string) {
	const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
	if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
	if (integration !== "GitHub") return true;
	await doPost(`${integrationUrl}/api/reset_cache`, {});
}
